import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../utils/supabase/client';
import { SUBSCRIPTION_PLANS } from '../features/subscription/constants';

export interface BillingTier {
  id: string;
  name: string;
  description: string;
  stripeProductId: string | null;
  stripePriceId: string | null;
  priceAmountPence: number;
  currency: string;
  billingFrequency: 'monthly' | 'weekly';
  monthlyCredits: number | null;
  features: string[];
  tierOrder: number;
  isPopular: boolean;
  metadata: Record<string, any>;
}

interface BillingContextValue {
  tiers: BillingTier[] | null;
  isLoading: boolean;
  error: Error | null;
  getTierById: (id: string) => BillingTier | undefined;
  getNormalizedTierId: (id?: string | null) => string | undefined;
}

const BillingContext = createContext<BillingContextValue | undefined>(undefined);

const CACHE_KEY = 'billing_tiers_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export function BillingProvider({ children }: { children: ReactNode }) {
  const [tiers, setTiers] = useState<BillingTier[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTiers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Try cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          setTiers(data);
          setIsLoading(false);
          return;
        }
      }

      // Fetch fresh via Supabase RPC
      const { data, error: rpcError } = await supabase.rpc('get_billing_tiers' as any);

      if (rpcError) throw new Error(`RPC failed: ${rpcError.message}`);

      // Transform snake_case to camelCase
      const transformed = (data || []).map((tier: any) => ({
        id: tier.id,
        name: tier.name,
        description: tier.description,
        stripeProductId: tier.stripe_product_id,
        stripePriceId: tier.stripe_price_id,
        priceAmountPence: tier.price_amount_pence,
        currency: tier.currency,
        billingFrequency: tier.billing_frequency,
        monthlyCredits: tier.monthly_credits,
        features: tier.features || [],
        tierOrder: tier.tier_order,
        isPopular: tier.is_popular,
        metadata: tier.metadata || {},
      }));

      setTiers(transformed);

      // Cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data: transformed, timestamp: Date.now() })
      );
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.warn('[BillingContext] Error fetching tiers:', err.message);

      // Fallback to cache even if stale
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          setTiers(JSON.parse(cached).data);
          setError(null); // Clear error if we have stale cache
          return;
        } catch {
          // Cache is invalid
        }
      }

      // Final fallback: use hardcoded SUBSCRIPTION_PLANS
      const fallbackTiers = SUBSCRIPTION_PLANS.map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        stripeProductId: null,
        stripePriceId: plan.priceId || null,
        priceAmountPence: Math.round(plan.priceValue * 100),
        currency: 'gbp',
        billingFrequency: plan.billingFrequency,
        monthlyCredits: plan.monthlyProjectCredits,
        features: plan.features,
        tierOrder: 0,
        isPopular: plan.popular ?? false,
        metadata: {},
      }));
      setTiers(fallbackTiers);
      console.warn('[BillingContext] Using hardcoded fallback tiers. RPC not available.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchTiers();
  }, []);

  // Refresh on app focus (only if cache is >6h old)
  useEffect(() => {
    const handleFocus = () => {
      const cached = localStorage.getItem(CACHE_KEY);
      const cacheAge = cached
        ? Date.now() - JSON.parse(cached).timestamp
        : Infinity;
      if (cacheAge > CACHE_TTL / 4) {
        fetchTiers();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const getTierById = (id: string): BillingTier | undefined => {
    if (!tiers) return undefined;
    const normalized = getNormalizedTierId(id);
    return tiers.find((t) => t.id === normalized);
  };

  const getNormalizedTierId = (id?: string | null): string | undefined => {
    if (!id) return undefined;
    const normalized = id.toLowerCase();
    const legacyMap: Record<string, string> = {
      essential: 'silver',
      professional: 'gold',
      enterprise: 'platinum',
    };
    return legacyMap[normalized] ?? normalized;
  };

  return (
    <BillingContext.Provider
      value={{ tiers, isLoading, error, getTierById, getNormalizedTierId }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be used within BillingProvider');
  return ctx;
}
